import { CloseIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Input,
  Portal,
  Tab,
  TabList,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';

import { SIDE_PANEL_WIDTH } from '../../constants/panelWidths';
import { TextContentEditor } from '../Chart/TextChart/TextContentEditor';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import { ChartCustomizePanel } from './ChartCustomizePanel';
import {
  ChartTypeSelector,
  isUserSelectableChartType,
} from './ChartTypeSelector';
import { getChartBuilderPanelVisibility } from './chart-builder-panel-visibility';
import { getEffectiveChartSelectorConfig } from './chart-selector-config';
import { DataFilterSelector } from './DataFilterSelector';
import { DimensionSourceSelector } from './DimensionSourceSelector';
import { MetricSelector } from './MetricSelector';
import { MultiMetricSelector } from './MultiMetricSelector';
import { ReportParameterSelector } from './ReportParameterSelector';
import { SecondaryDimensionSelector } from './SecondaryDimensionSelector';
import { SnowflakeParameterList } from './SnowflakeParameterList';

function getExcludedBreakdownDimensions(
  dimensionSource: DimensionSource | undefined
): SecondaryDimensionValues[] {
  if (!dimensionSource || dimensionSource === 'primary') {
    return [];
  }
  if (dimensionSource === 'competitors') {
    return ['company', 'competitors'];
  }
  return [dimensionSource];
}

type ChartConfigProps = {
  isOpen: boolean;
  item: PositionedItem | undefined;
  selectedIds?: Set<string>;
  clearSelection?: () => void;
  onClose: () => void;
};

export const ChartBuilderPanel = ({
  item,
  isOpen,
  selectedIds,
  clearSelection,
  onClose,
}: ChartConfigProps) => {
  const { dispatch, primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const customReportsSnowflakeEnabled = useUnleashFlag(
    FeatureFlag.CustomReportsSnowflake
  );

  const id = item?.id;
  const chartType = item?.plot?.chartType;
  const isSelectableChartType = chartType
    ? isUserSelectableChartType(chartType)
    : false;

  const metric = item?.plot?.defaultMetricOptions?.[0];
  const primaryFilterId = primaryFilter?.id;

  const selectorConfig = chartType
    ? getEffectiveChartSelectorConfig(chartType, {
        overrides: item?.plot?.selectorOverrides,
        metric,
        primaryFilterId,
      })
    : null;

  const isSnowflake = item?.plot?.dataSource === 'snowflake';

  const isBarChart = chartType === 'bar';
  const dimensionSource = item?.plot?.dimensionSource;
  const excludedBreakdownDimensions =
    getExcludedBreakdownDimensions(dimensionSource);

  const panelVisibility = getChartBuilderPanelVisibility({
    chartType,
    hasPrimaryFilter: Boolean(primaryFilter),
    isReport,
    customReportsSnowflakeEnabled,
    dataSource: item?.plot?.dataSource,
  });

  const title = item?.title ?? '';
  const isMultiSelect = selectedIds != null && selectedIds.size > 1;

  const handleDeleteChart = () => {
    const idsToDelete = (() => {
      if (selectedIds != null && selectedIds.size > 0) {
        return Array.from(selectedIds);
      }
      if (id !== null && id !== undefined) {
        return [id];
      }
      return [];
    })();
    if (idsToDelete.length === 0) return;
    dispatch({ type: 'DELETE_ITEMS', payload: { ids: idsToDelete } });
    clearSelection?.();
    onClose();
  };

  const handleSubmitTitle = (value: string) => {
    if (id && value !== item?.title) {
      const newTitle = value.trim() === '' ? null : value;
      dispatch({ type: 'UPDATE_ITEM_TITLE', payload: { id, title: newTitle } });
    }
  };

  const [accordionIndex, setAccordionIndex] = useState(-1);

  const handleAccordionChange = (index: number) => {
    setAccordionIndex(index);
  };

  useEffect(() => {
    if (item && !chartType && accordionIndex !== 0) {
      setAccordionIndex(0);
    }
  }, [item, chartType, accordionIndex]);

  const [chartTabIndex, setChartTabIndex] = useState(0);

  const renderMetricSelector = () => {
    if (item === undefined) return null;
    if (selectorConfig?.metricMode === 'multi' && !isSnowflake) {
      return (
        <MultiMetricSelector
          item={item}
          isDisabled={!isSelectableChartType}
          showUnitOfAnalysis={selectorConfig?.showUnitOfAnalysis ?? false}
        />
      );
    }
    if (selectorConfig?.metricMode === 'single') {
      return (
        <MetricSelector
          item={item}
          isDisabled={!isSelectableChartType}
          isReport={isReport}
        />
      );
    }
    return null;
  };

  const renderParameterSelector = () => {
    if (item === undefined) return null;
    if (isSnowflake && isReport && customReportsSnowflakeEnabled) {
      return (
        <SnowflakeParameterList
          item={item}
          isDisabled={!isSelectableChartType}
        />
      );
    }
    if (primaryFilter && selectorConfig?.showReportParameters && isReport) {
      return (
        <Box mt={3}>
          <ReportParameterSelector
            item={item}
            parameters={item.plot?.plotParameters ?? []}
            primaryFilter={primaryFilter}
          />
        </Box>
      );
    }
    return null;
  };

  return (
    <Portal>
      <Box
        position="fixed"
        top={0}
        right={0}
        height="100vh"
        width={SIDE_PANEL_WIDTH}
        display="flex"
        flexDirection="column"
        bg="white"
        boxShadow="lg"
        paddingTop={2}
        paddingBottom={8}
        px={2}
        transform={
          isOpen
            ? 'translate3d(0, 0, 0)'
            : `translate3d(${SIDE_PANEL_WIDTH}, 0, 0)`
        }
        transition="transform 0.3s ease-in-out"
        zIndex={100}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          mr={5}
          flexShrink={0}
        >
          <Text
            display="flex"
            alignItems="center"
            px={4}
            py={3}
            fontSize="xl"
            fontWeight="700"
          >
            {isMultiSelect
              ? `${selectedIds?.size ?? 0} charts selected`
              : 'Edit chart'}
          </Text>
          <IconButton
            aria-label="Close chart builder"
            icon={<CloseIcon boxSize={2.5} />}
            size="xs"
            variant="ghost"
            colorScheme="gray"
            onClick={onClose}
          />
        </Flex>

        {isMultiSelect ? (
          <Box paddingX={4} paddingTop={4} flexShrink={0}>
            <Button
              size="sm"
              width="100%"
              colorScheme="red"
              variant="outline"
              onClick={handleDeleteChart}
            >
              Delete {selectedIds?.size ?? 0} charts
            </Button>
          </Box>
        ) : (
          <>
            <Box paddingX={4} flexShrink={0}>
              <Tabs
                isFitted
                size="sm"
                colorScheme="green"
                index={chartTabIndex}
                onChange={(index) => setChartTabIndex(index)}
              >
                <TabList>
                  <Tab fontWeight="600">Setup</Tab>
                  <Tab fontWeight="600">Customize</Tab>
                </TabList>
              </Tabs>
            </Box>

            {chartTabIndex === 1 && item ? (
              <Box flex="1" minH={0} overflow="auto">
                <ChartCustomizePanel item={item} isReport={isReport} />
              </Box>
            ) : (
              <Box flex="1" minH={0} overflow="auto">
                {/* TODO: More actionable / useful empty state (only shows up after delete) */}
                {!item && isOpen && (
                  <Text fontSize="sm" fontWeight={500}>
                    Select or create a chart on the grid.
                  </Text>
                )}

                {item && (
                  <Accordion
                    allowToggle
                    index={[accordionIndex]}
                    onChange={handleAccordionChange}
                    mb={3}
                    paddingX={2}
                  >
                    <AccordionItem border="none">
                      <AccordionButton
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        paddingLeft={2}
                        paddingRight=".625rem"
                        py={2}
                      >
                        <Box
                          flex="1"
                          textAlign="left"
                          fontSize="sm"
                          fontWeight="600"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Text fontSize="sm" fontWeight="600">
                            Chart Type
                          </Text>
                          {!!chartType && !isSelectableChartType && (
                            <Tooltip
                              label="This chart's settings are fixed and cannot be changed."
                              fontSize="xs"
                            >
                              <span>
                                <InfoOutlineIcon color="gray.400" />
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                        <Flex paddingRight={2.5}>
                          <AccordionIcon ml={2} />
                        </Flex>
                      </AccordionButton>
                      <AccordionPanel marginTop={2} paddingY={0} paddingX={2}>
                        <ChartTypeSelector
                          onFirstSelect={() => setAccordionIndex(-1)}
                          item={item}
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}

                <Box paddingX={4}>
                  {panelVisibility.showChartOptionBlock && item && (
                    <>
                      {renderMetricSelector()}

                      {isSnowflake &&
                        isReport &&
                        customReportsSnowflakeEnabled &&
                        chartType === 'table' &&
                        id && (
                          <Button
                            size="xs"
                            variant="link"
                            marginBottom={3}
                            onClick={() => {
                              dispatch({
                                type: 'UPDATE_ITEM_DATASOURCE',
                                payload: { id, dataSource: 'mw-gql' },
                              });
                            }}
                          >
                            Use standard table metrics
                          </Button>
                        )}

                      {primaryFilter && selectorConfig?.showSegment && (
                        <SecondaryDimensionSelector
                          item={item}
                          primaryFilter={primaryFilter}
                          forceSegment={selectorConfig?.forceSegment ?? false}
                          showGroupBySegment={
                            selectorConfig?.showGroupBySegment ?? false
                          }
                          isDisabled={!isSelectableChartType}
                          excludeDimensions={
                            isBarChart ? excludedBreakdownDimensions : undefined
                          }
                        />
                      )}

                      {primaryFilter && selectorConfig?.showComparison && (
                        <DimensionSourceSelector
                          item={item}
                          primaryFilter={primaryFilter}
                        />
                      )}

                      {selectorConfig?.showFilter && (
                        <DataFilterSelector
                          item={item}
                          plotFilterMenu={
                            selectorConfig.plotFilterMenu ?? 'all'
                          }
                        />
                      )}

                      {renderParameterSelector()}
                    </>
                  )}

                  {chartType === 'text' && id && primaryFilter?.id && item && (
                    <Box mb={2}>
                      <TextContentEditor
                        item={item}
                        value={item.plot?.textHtml || ''}
                        onChange={(textHtml) => {
                          dispatch({
                            type: 'UPDATE_ITEM_TEXT_HTML',
                            payload: { id, textHtml },
                          });
                        }}
                        primaryFilterId={primaryFilter.id}
                      />
                      {primaryFilter && isReport && (
                        <Box mt={3}>
                          <ReportParameterSelector
                            item={item}
                            parameters={item.plot?.plotParameters ?? []}
                            primaryFilter={primaryFilter}
                          />
                        </Box>
                      )}
                      <Box mt={3}>
                        <Checkbox
                          isChecked={
                            item.plot?.excludeFromExecutiveSummary ?? false
                          }
                          onChange={(e) => {
                            dispatch({
                              type: 'UPDATE_ITEM_EXCLUDE_FROM_EXECUTIVE_SUMMARY',
                              payload: {
                                id,
                                excludeFromExecutiveSummary: e.target.checked,
                              },
                            });
                          }}
                        >
                          <Text fontSize="sm">
                            Exclude from executive summary
                          </Text>
                        </Checkbox>
                      </Box>
                    </Box>
                  )}

                  {chartType === 'gauge' && id && item && (
                    <Box mb={2}>
                      <Text
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Value
                      </Text>
                      <Input
                        value={item.plot?.gaugeValue ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_VALUE',
                            payload: { id, gaugeValue: e.target.value },
                          });
                        }}
                        placeholder='Enter value (0-1) or handlebar template (e.g., {{FORMAT (SUM "headcount") format="SI"}})'
                        fontSize="sm"
                      />
                      <Text
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Primary Text
                      </Text>
                      <Textarea
                        value={item.plot?.gaugePrimaryText || ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_PRIMARY_TEXT',
                            payload: { id, gaugePrimaryText: e.target.value },
                          });
                        }}
                        placeholder='Enter primary text with handlebar syntax (e.g., {{FORMAT (SUM "headcount") format="SI"}})'
                        fontSize="sm"
                        minH="80px"
                        resize="vertical"
                      />
                      <Text
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Primary label
                      </Text>
                      <Input
                        value={item.plot?.gaugePrimaryLabel ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_PRIMARY_LABEL',
                            payload: { id, gaugePrimaryLabel: e.target.value },
                          });
                        }}
                        placeholder="Optional label to the right of primary text (e.g. % or handlebar)"
                        fontSize="sm"
                      />
                      <Text
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Sub Text
                      </Text>
                      <Textarea
                        value={item.plot?.gaugeSubText || ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_SUB_TEXT',
                            payload: { id, gaugeSubText: e.target.value },
                          });
                        }}
                        placeholder="Enter sub text with handlebar syntax"
                        fontSize="sm"
                        minH="60px"
                        resize="vertical"
                      />
                    </Box>
                  )}

                  {item && (
                    <>
                      <Text
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Title
                      </Text>
                      <Input
                        placeholder="Untitled chart"
                        value={title}
                        onChange={(e) => handleSubmitTitle(e.target.value)}
                        variant="outline"
                        paddingX="0.75rem"
                        h="29px"
                        lineHeight="29px"
                        fontSize="sm"
                        isReadOnly={!isSelectableChartType}
                      />

                      <Button
                        size="sm"
                        width="100%"
                        marginTop="1rem"
                        colorScheme="red"
                        variant="outline"
                        onClick={handleDeleteChart}
                      >
                        Delete chart
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Portal>
  );
};
